// Built-in canvases — the curated, read-only base set shipped with the
// dashboard. Same model as builtinKernels.js / the built-in signal set: hard-
// coded in code, never persisted to localStorage, but browsable / mountable /
// forkable wherever a user canvas is. Forking one (canvasTemplateStore
// .forkCanvasTemplate) writes a mutable user-owned copy.
//
// A canvas template is the shape documented in canvasTemplateStore.js, plus a
// `builtin: true` marker. Bundled cells carry the referenced kernel UUID and a
// temporary command mirror for the command-keyed runtime.
//
// These power the scope-aware "Canvases" lens (components/lenses/CanvasesBody):
// the sidebar entry beside Overview / X-ray / Inventory. Opened at the root the
// widgets render tenant-wide; opened inside a drilled scope, each scoped kernel
// re-runs narrowed to that endpoint / cluster / provider / user. The canvas
// lineup is guided by the dashboard's sample starter prompts.

import { BUILTIN_CANVAS_IDS, BUILTIN_KERNEL_IDS } from "./bundledArtifactIds.js";

// Fixed identity — built-ins are shipped constants, not authored at runtime.
// A descending nudge by array index keeps the shipped order stable under a
// sort-by-updatedAt (most-recent-first) without colliding with live user edits.
const BUILTIN_TS = Date.UTC(2026, 0, 1);

// Authoring helpers: a cell references a kernel by command with an optional
// caption; a row groups cells that render side by side.
const cell = (kernelId, command, caption) => ({ kernel_id: kernelId, command, caption });
const row = (...cells) => ({ cells });

export const BUILTIN_CANVAS_RUN_CONCURRENCY = Object.freeze({
    "security-posture": 4,
});

const c = (index, { id, slug, title, description, rows, runtime = null }) => ({
    id,
    slug,
    title,
    description,
    builtin: true,
    rows,
    ...(runtime ? { runtime } : {}),
    createdAt: BUILTIN_TS,
    // Descending by declaration order so the gallery keeps its intended order.
    updatedAt: BUILTIN_TS - index,
});

export const BUILTIN_CANVASES = [
    c(0, {
        id: BUILTIN_CANVAS_IDS["security-posture"],
        slug: "security-posture",
        title: "Security posture",
        description: "A CISO view of observed AI coverage, exposure, use, governance evidence, and security Signals.",
        rows: [
            row(
                cell(
                    BUILTIN_KERNEL_IDS["security-activity-summary"],
                    "security-activity-summary",
                    "Endpoint coverage",
                ),
                cell(
                    BUILTIN_KERNEL_IDS["security-volume-summary"],
                    "security-volume-summary",
                    "AI activity, last 7 days",
                ),
            ),
            row(
                cell(
                    BUILTIN_KERNEL_IDS["endpoints-with-signals"],
                    "endpoints-with-signals",
                    "Endpoints with the most Signals, last 7 days",
                ),
                cell(
                    BUILTIN_KERNEL_IDS["loudest-signals"],
                    "loudest-signals",
                    "Signal volume and reach, last 7 days",
                ),
            ),
            row(
                cell(
                    BUILTIN_KERNEL_IDS["signal-rate-by-provider"],
                    "signal-rate-by-provider",
                    "Signal rate by AI provider, last 7 days",
                ),
                cell(
                    BUILTIN_KERNEL_IDS["signal-bearing-work"],
                    "signal-bearing-work",
                    "AI work associated with Signals, last 7 days",
                ),
            ),
            row(
                cell(
                    BUILTIN_KERNEL_IDS["installed-agent-footprint"],
                    "installed-agent-footprint",
                    "Installed AI agents",
                ),
                cell(
                    BUILTIN_KERNEL_IDS["agent-use-concentration"],
                    "agent-use-concentration",
                    "AI activity by provider, last 7 days",
                ),
            ),
            row(cell(
                BUILTIN_KERNEL_IDS["provider-footprint"],
                "provider-footprint",
                "Model-provider footprint by policy, last 30 days",
            )),
            row(
                cell(
                    BUILTIN_KERNEL_IDS["local-ai-summary"],
                    "local-ai-summary",
                    "Local AI presence, last 30 days",
                ),
                cell(
                    BUILTIN_KERNEL_IDS["local-ai-detail"],
                    "local-ai-detail",
                    "Local AI software by endpoint, last 30 days",
                ),
            ),
            row(cell(
                BUILTIN_KERNEL_IDS["observed-work-clusters"],
                "observed-work-clusters",
                "Observed work categories, last 30 days",
            )),
            row(
                cell(
                    BUILTIN_KERNEL_IDS["mcp-server-prevalence"],
                    "mcp-server-prevalence",
                    "Most prevalent MCP servers",
                ),
                cell(
                    BUILTIN_KERNEL_IDS["skill-prevalence"],
                    "skill-prevalence",
                    "Most prevalent skills",
                ),
                cell(
                    BUILTIN_KERNEL_IDS["memory-prevalence"],
                    "memory-prevalence",
                    "Most prevalent memories",
                ),
            ),
            row(cell(
                BUILTIN_KERNEL_IDS["visibility-control-gaps"],
                "visibility-control-gaps",
                "Policy enforcement gaps, last 30 days",
            )),
        ],
    }),
    c(1, {
        id: BUILTIN_CANVAS_IDS["ai-usage-overview"],
        slug: "ai-usage-overview",
        title: "AI usage overview",
        description: "AI activity, agent surfaces, providers, and models in the current scope.",
        rows: [
            row(cell(BUILTIN_KERNEL_IDS["activity-summary"], "activity-summary", "Headline counts for this scope (30d)")),
            row(cell(BUILTIN_KERNEL_IDS["prompt-volume"], "prompt-volume", "Daily prompt activity")),
            row(
                cell(BUILTIN_KERNEL_IDS["agent-surfaces"], "agent-surfaces", "Which agent harnesses are driving the work"),
                cell(BUILTIN_KERNEL_IDS["model-mix"], "model-mix", "Top models by prompt count"),
            ),
            // provider-mix is a treemap — always full-width (see SOLO_ROW_KINDS).
            row(cell(BUILTIN_KERNEL_IDS["provider-mix"], "provider-mix", "Share of prompts by provider")),
        ],
    }),
    c(2, {
        id: BUILTIN_CANVAS_IDS["adoption-and-concentration"],
        slug: "adoption-and-concentration",
        title: "Adoption & concentration",
        description: "Usage concentration, tool adoption, and newly observed models in the current scope.",
        rows: [
            row(cell(BUILTIN_KERNEL_IDS["activity-summary"], "activity-summary", "Headline counts for this scope (30d)")),
            row(
                cell(BUILTIN_KERNEL_IDS["top-endpoints"], "top-endpoints", "Endpoints with the most activity"),
                cell(BUILTIN_KERNEL_IDS["tool-leaders"], "tool-leaders", "Tool & MCP-tool sprawl by distinct prompts"),
            ),
            row(cell(BUILTIN_KERNEL_IDS["new-models"], "new-models", "Newly-seen models (90d), newest first")),
        ],
    }),
    c(3, {
        id: BUILTIN_CANVAS_IDS["shadow-ai"],
        slug: "shadow-ai",
        title: "Shadow AI",
        description: "Which AI providers endpoints reach by DNS, and which machines run a model on their own hardware.",
        rows: [
            row(cell(BUILTIN_KERNEL_IDS["provider-footprint"], "provider-footprint", "Endpoints observed resolving each provider (DNS evidence, 30d), colored by tenant policy; a floor, cached lookups go unseen; tenant-wide")),
            // Local AI software can be invisible to provider-side monitoring.
            // Summary first, then how it was detected, then which machines.
            row(cell(BUILTIN_KERNEL_IDS["local-ai-summary"], "local-ai-summary", "Endpoints with local AI software (30d); tenant-wide")),
            row(cell(BUILTIN_KERNEL_IDS["local-ai-evidence"], "local-ai-evidence", "Endpoints per evidence class — a GPU-backed load is stronger than a runtime that merely started")),
            row(cell(BUILTIN_KERNEL_IDS["local-ai-detail"], "local-ai-detail", "Local AI software by endpoint, most recently active first")),
            // The process-execution arm on its own terms: process-execution rows
            // carry no library, so local-ai-detail shows them only as a
            // runtime with a blank library. This breaks out the executed binary.
            row(cell(BUILTIN_KERNEL_IDS["local-ai-runtime-exec"], "local-ai-runtime-exec", "Recent local runtime activity by endpoint; open one for exact image and execution counts")),
        ],
    }),
    c(4, {
        id: BUILTIN_CANVAS_IDS["tools-and-resources"],
        slug: "tools-and-resources",
        title: "Tools & resources",
        description: "Tools and resources used by agents in the current scope. Security signal findings are available in the Alerts console.",
        rows: [
            row(cell(BUILTIN_KERNEL_IDS["activity-summary"], "activity-summary", "Headline counts for this scope (30d)")),
            row(
                cell(BUILTIN_KERNEL_IDS["tool-leaders"], "tool-leaders", "Most-used tools & MCP tools, by distinct prompts"),
                cell(BUILTIN_KERNEL_IDS["resource-leaders"], "resource-leaders", "Files & resource paths agents touched most"),
            ),
        ],
    }),
    c(5, {
        id: BUILTIN_CANVAS_IDS["token-spend"],
        slug: "token-spend",
        title: "Token spend",
        description: "Tenant-wide token volume by model and client surface. These widgets do not follow the current drilldown.",
        rows: [
            row(cell(BUILTIN_KERNEL_IDS["token-summary"], "token-summary", "Tenant-wide token volume (7d)")),
            row(
                cell(BUILTIN_KERNEL_IDS["token-by-model"], "token-by-model", "Tenant-wide tokens by model"),
                cell(BUILTIN_KERNEL_IDS["token-by-surface"], "token-by-surface", "Tenant-wide output tokens by client surface"),
            ),
        ],
    }),
];

export const BUILTIN_CANVAS_SLUGS = new Set(BUILTIN_CANVASES.map((t) => t.slug));
